import React, { useState } from 'react';
import { Layout, Seo } from '@layout';
import { weddingInvitationsPageData as pageData } from '@data-ts/pages/wedding-invitations';
import { graphql } from 'gatsby';
import {
  HighQualityPrintsSection,
  InvitationsReimaginedSection,
  ConnectedGuestListSection,
  SmartRSVPSection,
  EcoSection,
  SmartRSVPSectionV2,
} from '@sections/wedding-invitations';
import { ColoredBlockSection } from '@sections/shared/ColoredBlockSection';
import { HighQualityPrintsSection as HighQualityPrintsSectionV2 } from '@sections/shared/HighQualityPrintsSection';
import { faqQuestionsData } from '@data-ts/components/FaqQuestions';
import { FaqList } from '@components/FaqList';
import { AbcSection } from '@sections/shared/AbcSection';
import { GalleryPageContent } from '@components/GalleryComponents/GalleryPageContent/GalleryPageContent';
import { invitationDesignsCanonicalUrl } from '@utils/gallery.util';
import { productTypes } from '@data-ts/components/GalleryComponents/GalleryPageContent';
import { useMemo } from 'react';
import { createFilterGroupsProcessor, useProcessPrintCards } from '@utils/joyPrint.utils';
import { ABExperimentName, experiments } from '@utils/experiments/experiments';
import { useExperimentsTelemetry } from '@utils/experiments/useExperimentsTelemetry';
import { HowItWorksSection } from '@sections/shared/HowItWorksSection';
import { FAQSection } from '@sections/shared/FAQSection';
import { HeroSectionWithProps } from '@sections/shared/HeroSectionWithProps';
import { ValuePropsArg } from '@components/ValuePropsSection';

export const query = graphql`
  {
    bliss {
      stationeryTemplatesByCategory(category: invitation) {
        themeId
        thumbnailUrl
        approved
        enabled
      }
      themeVariants(variantTypes: [invite]) {
        variationType
        imageUrl
        printUrl
        vendor
        tags
        colors
        font
        variantColor
        variantName
        variantThemeId
        theme {
          id
          name
          themeId
          marketingData {
            variationType
            imageUrl
            printUrl
            vendor
            tags
            colors
            font
            variantColor
            variantName
            variantThemeId
            theme {
              name
              themeId
            }
          }
        }
      }
    }
  }
`;

export const WEDDING_INVITATIONS_VALUE_PROPS: ValuePropsArg[] = [
  {
    id: 'designDreamCards',
    icon: 'designCard',
    title: 'Design Your Dream Cards',
    description: 'Endless designs to match <br/> your wedding style and the <br/> best customization tools.',
  },
  {
    id: 'premiumPaperAndDigital',
    icon: 'premiumPaper',
    title: 'Premium Paper & Digital',
    description: 'From traditional paper <br/> cards to free online Save <br/> the Dates, do it all.',
  },
  {
    id: 'addressingMadeEasy',
    icon: 'addressing',
    title: 'Addressing Made Easy',
    description: 'Save time getting guest <br/> info with the magic of <br/> Contact Collector.',
  },
  {
    id: 'matchWeddingWebsite',
    icon: 'weddingWebsiteIcon',
    title: 'Match Wedding Website',
    description: 'Seamlessly design your <br/> Save the Dates to match <br/> your wedding website.',
  },
];

export const WEDDING_INVITATIONS_VALUE_PROPS_MOBILE: ValuePropsArg[] = [
  {
    id: 'designDreamCards',
    icon: 'designCard',
    title: 'Design Your <br/> Dream Cards',
  },
  {
    id: 'premiumPaperAndDigital',
    icon: 'premiumPaper',
    title: 'Premium <br/> Paper & Digital',
  },
  {
    id: 'addressingMadeEasy',
    icon: 'addressing',
    title: 'Addressing <br/> Made Easy',
  },
  {
    id: 'matchWeddingWebsite',
    icon: 'weddingWebsiteIcon',
    title: 'Match Wedding <br/> Website',
  },
];

const CATEGORIES_IMAGES_DATA = {
  bold: 'invites-gallery/styles/bold.png',
  classic: 'invites-gallery/styles/classic.png',
  cultural: 'invites-gallery/styles/cultural.png',
  destination: 'invites-gallery/styles/destination.png',
  'floral-and-botanical': 'invites-gallery/styles/floral_botanical.png',
  metallic: 'invites-gallery/styles/metallic.png',
  modern: 'invites-gallery/styles/modern.png',
  photo: 'invites-gallery/styles/photo.png',
  rustic: 'invites-gallery/styles/rustic.png',
  seasonal: 'invites-gallery/styles/seasonal.png',
  textured: 'invites-gallery/styles/textured.png',
};

const page: MarcomPageName = 'wedding-invitations';

const WeddingInvitationsDesigns = ({ data }: { data: BlissGalleryECardsThemesData }) => {
  const { faq, highQualityPrintsSectionV2, howItWorksSection, hero } = pageData;

  const { cards, setShowDesignsWithMatchingPrints } = useProcessPrintCards(data, 'invite');
  const [chromeless, setChromeless] = useState(false);

  const processFilterGroups = useMemo(() => {
    return createFilterGroupsProcessor(productTypes.saveTheDate, data.bliss);
  }, [data]);

  const WeddingInvitationExperimentVariant = experiments.WeddingInvitationExperiment.Variant.Component;
  const WeddingInvitationExperimentControl = experiments.WeddingInvitationExperiment.Control.Component;

  useExperimentsTelemetry({
    name: ABExperimentName.WeddingInvitationExperiment,
  });
  return (
    <Layout
      isTransparentHeader={true}
      transparentJoyLogoColor="mono7"
      transparentHeaderButtonVariant="defaultPrimeWeddingWebsites"
      hideValueProps
      page={page}
      chromeless={chromeless}
    >
      <Seo
        title={pageData.seo.title}
        description={pageData.seo.content}
        canonicalURL={invitationDesignsCanonicalUrl}
        ogImage={'common/meta_share/invites.jpg'}
      />
      <WeddingInvitationExperimentControl>
        <GalleryPageContent
          page={page}
          productType={productTypes.invitation}
          cards={cards}
          processFilterGroups={processFilterGroups}
          onShowDesignsWithMatchingPrintsToggle={setShowDesignsWithMatchingPrints}
          showModal={isModalOpened => {
            setChromeless(isModalOpened);
          }}
        />
        <HighQualityPrintsSection />
        <InvitationsReimaginedSection />
        <SmartRSVPSection />
        <ConnectedGuestListSection />
        <EcoSection />
        <ColoredBlockSection
          isLeftAligned
          backgroundColor="humana1Light"
          sideHeaderText={'Frequently <br/> Asked <br/> Questions'}
          sectionId="Wedding_Invitation_Design_FAQ"
          isToggleNeeded={true}
        >
          <FaqList
            accordion={true}
            questionNodes={[...faqQuestionsData.weddingInvitationDesign, ...faqQuestionsData.general]}
            color="neutra6"
          />
        </ColoredBlockSection>
      </WeddingInvitationExperimentControl>
      <WeddingInvitationExperimentVariant>
        <HeroSectionWithProps
          page={page}
          heroData={hero}
          valueProps={WEDDING_INVITATIONS_VALUE_PROPS}
          valuePropsMobile={WEDDING_INVITATIONS_VALUE_PROPS_MOBILE}
          valuePropsElementId="wedding-invitations-props"
          sectionId="Wedding_Invitations_Hero"
          ctaButtonId="Wedding_Invitations_Hero_CTA"
        />
        <GalleryPageContent
          page={page}
          productType={productTypes.invitation}
          cards={cards}
          processFilterGroups={processFilterGroups}
          onShowDesignsWithMatchingPrintsToggle={setShowDesignsWithMatchingPrints}
          showModal={isModalOpened => {
            setChromeless(isModalOpened);
          }}
          displayHeroSection={false}
          initialProductCount={12}
          displayFilterTab={false}
          defaultProductType={productTypes.prints}
          sectionId="gallery-grid-wrapper"
          displayCategoryImages
          categoryImagesData={CATEGORIES_IMAGES_DATA}
          hideCategoryImagesScrollbar
        />
        <HighQualityPrintsSectionV2 highQualityPrintsSectionData={highQualityPrintsSectionV2} />
        <HowItWorksSection howItWorksSectionData={howItWorksSection} />
        <SmartRSVPSectionV2 />
        <ConnectedGuestListSection />
        <FAQSection
          id="wedding-invites-faq"
          title={faq.title}
          description={faq.description}
          questionNodes={faqQuestionsData.weddingInvitationDesignV2}
        />
      </WeddingInvitationExperimentVariant>
      <AbcSection page={page} abcSectionId="Wedding_Invitation_Design_ABC_CTA" />
    </Layout>
  );
};

export default WeddingInvitationsDesigns;
